import s3URL from '@mathflat/common/util/s3'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { isBefore } from 'date-fns'
import { makeAutoObservable, runInAction } from 'mobx'
import { generatePath } from 'react-router'

import ScenarioCompletePopup from '@/contentComponents/global/Scenario/_components/ScenarioCompletePopup'
import ScenarioPopup from '@/contentComponents/global/Scenario/_components/ScenarioPopup'
import { scenarioController } from '@/controller/scenario.controller'
import type { User } from '@/dto'
import { Scenario, SubScenario } from '@/dto'
import { CheckableColl } from '@/models/CheckableColl'
import CheckableMap from '@/models/CheckableMap'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { envUtils } from '@/utils/envUtils'
import { commonErrorMessage } from '@/utils/toast'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import modalService from './modal.service'

export class ScenarioService {
  private _isProfileShow = false

  scenarioColl = new CheckableColl<Scenario>(
    CheckableMap.fromArr([] as Scenario[], 'id' as keyof Scenario),
  )
  subScenarioColl = new CheckableColl<SubScenario>(
    CheckableMap.fromArr([] as SubScenario[], 'id' as keyof SubScenario),
  )
  bonusScenarioColl = new CheckableColl<Scenario>(
    CheckableMap.fromArr([] as Scenario[], 'id' as keyof Scenario),
  )
  scenarioPointerAt: { 단계?: Scenario['id'] | undefined; path?: string } = { 단계: undefined }

  // 프론트에서 static하게 데이터 관리.
  // 아이디는 백엔드에서 의존하고 있으므로, 절대 바뀌면 안됨.
  // 프로토타입으로 빠르게 개발목표, 버저닝 고려 안함. 버저닝 하게 되면 뒤에 _v2붙힐것
  static scenarioIds = {
    학습지만들기: '학습지만들기',
    학습지출제하기: '학습지출제하기',
    학습지채점하기: '학습지채점하기',
    보고서만들기: '보고서만들기',
    오답학습지출제: '오답학습지출제',
    학습지보고서인쇄: '학습지보고서인쇄',
    자동채점: '자동채점',
    '시중교재,유사/쌍둥이문제': '시중교재,유사/쌍둥이문제',
    문제즐겨찾기: '문제즐겨찾기',
    학생출결앱: '학생출결앱',
    선생님추가: '선생님추가',
    반만들기: '반만들기',
    그룹보고서: '그룹보고서',
    다른기관학습지: '다른기관학습지',
    학생학부모앱: '학생학부모앱',
  } as const

  constructor() {
    makeAutoObservable(this)
  }

  get isProfileShow() {
    return this._isProfileShow
  }

  get totalCount() {
    return this.scenarioColl.size
  }

  get progressRate() {
    return (this.scenarioColl.toCheckedValues.length / this.totalCount) * 100
  }

  get nextScenario() {
    return this.scenarioColl.checkSize < this.scenarioColl.size
      ? this.scenarioColl.toArr[this.scenarioColl.checkSize]
      : this.bonusScenarioColl.toArr[this.bonusScenarioColl.checkSize]
  }

  get enabled() {
    const { pathname } = window.location
    if (
      !commonRepo.academy ||
      !commonRepo.academy.academyId ||
      pathname === RouteName.serverMaintenance ||
      commonRepo.isSeoltab
    ) {
      return false
    }

    const abTestStartDate = envUtils.isLive ? new Date(2023, 6, 19) : new Date(2023, 6, 1)
    // 배포일(abTestStartDate) 이전이거나, 배포일 이전부터 무료체험시작한 사람들일 경우,
    // 현재 무료체험이기만 하면 무조건 enabled
    if (
      isBefore(new Date(), abTestStartDate) ||
      (typeof commonRepo.academy.freeStartDate === 'string' &&
        commonRepo.academy.freeStartDate !== '' &&
        isBefore(new Date(commonRepo.academy.freeStartDate), abTestStartDate))
    ) {
      return commonRepo.academy.payStatus === '무료체험'
    }

    // 배포일 이후
    // 시나리오 노출 대상 = D코드가 짝수인 사람
    const isABTestTarget = true

    return commonRepo.academy.payStatus === '무료체험' && isABTestTarget
  }

  initialize({
    appName,
    isSchoolflat,
  }: {
    appName: typeof commonRepo.appName
    isSchoolflat: typeof commonRepo.isSchoolflat
  }) {
    this.scenarioColl = new CheckableColl<Scenario>(
      CheckableMap.fromArr(
        [
          new Scenario({
            id: ScenarioService.scenarioIds.학습지만들기,
            프로필: {
              descHTML: (name: User['name']) =>
                `<span><b>${name}&nbsp;선생님</b><br/>벌써! 관리 잘하는 ${isSchoolflat ? '선생님' : '학원'}으로 입소문 났어요!<span>`,
            },
            시나리오카드: {
              title: '1단계 학습지 만들기',
              descHTML: `<span>정말 쉬워요!<br/>오늘 ${tJosa('출제')}할 문제지가</br>10초 만에 간단하게 뚝딱!</span>`,
            },
            완료팝업: {
              title: '1단계 학습지 만들기 정복!',
              descHTML: `<span>시중교재와 교과서 문제로도 학습지를 만들 수 있어요!</span>`,
              ctaButton: {
                text: '다음 기능 정복하기',
                width: 134,
              },
            },
            기능설명팝업: {
              preTitle: `${appName} 정복시작`,
              title: '1단계 학습지 만들기',
              type: '이미지',
              resourceUrl: s3URL.mathflatAssetsBucket.teacher(
                'images/scenario/scenario_popup_make_worksheet.png',
              ),
              ctaButton: {
                text: '학습지 만들어 보기',
                onClick: (scenarioId) => {
                  this.scenarioPointerAt = {
                    단계: scenarioId,
                    path: RouteName.academyShareScopeWorksheet,
                  }

                  commonRepo.router.push(RouteName.academyShareScopeWorksheet)
                },
                width: 144,
              },
            },
            order: 0,
          }),
          new Scenario({
            id: ScenarioService.scenarioIds.학습지출제하기,
            프로필: {
              descHTML: (name: User['name']) =>
                `<span><b>${name}&nbsp;선생님</b>의<br/>수업 준비 시간이 반으로 줄었네요!<span>`,
            },
            시나리오카드: {
              title: `2단계 학습지 ${tJosa('출제')}하기`,
              descHTML: `<span>학생마다 진도와<br/>레벨에 맞는 문제로 숙제를<br/>내줄 수 있어요!</span>`,
            },
            완료팝업: {
              title: `2단계 학습지 ${tJosa('출제')}하기 정복!`,
              descHTML: `<span>${tJosa('출제')}한 학습지는 선생님의 ${isSchoolflat ? '학교' : '학원'} 로고를 넣어 인쇄도 할 수 있어요!</span>`,
              ctaButton: {
                text: '다음 기능 정복하기',
                width: 134,
              },
            },
            기능설명팝업: {
              preTitle: '학습지를 만드셨다면?',
              title: `2단계 학습지 ${tJosa('출제')}하기`,
              descHTML: `문제를 ${tJosa('출제')}하면 학생이 학생앱에서 문제를 풀 수 있어요!<br/>학생앱은 <b>앱스토어</b>나 <b>구글 스토어</b>에서 '${commonRepo.appKorName}'을 검색해주세요.`,
              type: '이미지',
              resourceUrl: s3URL.mathflatAssetsBucket.teacher(
                'images/scenario/scenario_popup_assign.png',
              ),
              ctaButton: {
                text: `학습지 ${tJosa('출제')}해 보기`,
                onClick: (scenarioId) => {
                  this.scenarioPointerAt = {
                    단계: scenarioId,
                    path: RouteName.academyShareScopeWorksheet,
                  }

                  commonRepo.router.push(RouteName.academyShareScopeWorksheet)
                  scrollToTop()
                },
                width: 144,
              },
            },
            order: 1,
          }),
          new Scenario({
            id: ScenarioService.scenarioIds.학습지채점하기,
            프로필: {
              descHTML: (name: User['name']) =>
                `<span><b>${name}&nbsp;선생님</b>도<br/>1:1 맞춤 학습 관리가 가능해졌어요!<span>`,
            },
            시나리오카드: {
              title: '3단계 학습지 채점하기',
              descHTML: `<span>클릭 몇 번으로 끝내는 채점!<br/>더 이상 채점에 시간 뺏기지마세요!</span>`,
            },
            완료팝업: {
              title: '3단계 학습지 채점하기 정복!',
              descHTML: `<span>자동으로 채점되는 학생앱도 스토어에서 다운로드해 보세요!</span>`,
              ctaButton: {
                text: '다음 기능 정복하기',
                width: 134,
              },
            },
            기능설명팝업: {
              preTitle: `학습지를 ${tJosa('출제')}했다면?`,
              title: '3단계 학습지 채점하기',
              type: '이미지',
              resourceUrl: s3URL.mathflatAssetsBucket.teacher(
                'images/scenario/scenario_popup_correction.png',
              ),
              ctaButton: {
                text: '학습지 채점하러 가기',
                onClick: async (scenarioId) => {
                  const lastAssignedStudentId =
                    await scenarioService.fetchLastAssginedStudentIdHandler()
                  if (lastAssignedStudentId) {
                    const path = generatePath(RouteName.lessonWorksheetStudent, {
                      studentId: lastAssignedStudentId,
                    })
                    this.scenarioPointerAt = { 단계: scenarioId, path }

                    commonRepo.router.push(path)
                    scrollToTop()
                  }
                },
                width: 146,
              },
            },
            order: 2,
          }),
          new Scenario({
            id: ScenarioService.scenarioIds.보고서만들기,
            프로필: {
              descHTML: (name: User['name']) =>
                `<span><b>${name}&nbsp;선생님</b>의<br/>수업 준비 시간이 더 여유로워졌네요<span>`,
            },
            시나리오카드: {
              title: '4단계 보고서 만들기',
              descHTML: `<span>학생의 성적 상승을<br/>체계적으로 보여주는 전문가<br/>${isSchoolflat ? '학교가' : '학원이'} 되는 방법!</span>`,
            },
            완료팝업: {
              title: '4단계 보고서 만들기 정복!',
              descHTML: `<span>드디어 ${commonRepo.appKorName} 100% 완전 정복! 더 알아보고 싶은 기능이 있다면?</span>`,
              ctaButton: {
                text: '더 알아보고 싶은 기능 좋아요 누르러 가기',
                width: 262,
              },
            },
            기능설명팝업: {
              preTitle: '학습지를 채점했다면?',
              title: '4단계 보고서 만들기',
              type: '이미지',
              resourceUrl: s3URL.mathflatAssetsBucket.teacher(
                'images/scenario/scenario_popup_report.png',
              ),
              ctaButton: {
                text: '채점한 학생 보고서 만들어 보기',
                onClick: async (scenarioId) => {
                  const lastScoredStudentId =
                    await scenarioService.fetchLastScoredStudentIdHandler()
                  if (lastScoredStudentId) {
                    const path = generatePath(RouteName.lessonReportStudent, {
                      studentId: lastScoredStudentId,
                    })
                    this.scenarioPointerAt = { 단계: scenarioId, path }

                    commonRepo.router.push(path)
                    scrollToTop()
                  }
                },
                width: 204,
              },
            },
            order: 3,
          }),
        ],
        'id' as keyof Scenario,
      ),
    )
    this.subScenarioColl = new CheckableColl<SubScenario>(
      CheckableMap.fromArr(
        [
          new SubScenario({
            id: '학습지보고서인쇄',
            title: '학습지 보고서 인쇄',
            descHTML: `${commonRepo.appKorName}으로 만든 수업 자료를 인쇄해보세요!`,
            order: 0,
          }),
          new SubScenario({
            id: '자동채점',
            title: '자동 채점 ',
            descHTML: `채점은 ${commonRepo.appKorName}이 할게요! 선생님은 수업에만 집중하세요!`,
            order: 1,
          }),
          new SubScenario({
            id: '시중교재,유사/쌍둥이문제',
            title: '시중 교재, 유사/쌍둥이 문제',
            descHTML: '좋은 문제는 다양하게 변형해서 학생들이 또 풀게 해주세요!',
            order: 2,
          }),
          new SubScenario({
            id: '문제즐겨찾기',
            title: '문제 즐겨 찾기',
            descHTML: '반드시 풀게 해야 할 문제가 있다면 따로 저장해 둘 수 있어요! ',
            order: 3,
          }),
          new SubScenario({
            id: '학생출결앱',
            title: '학생 출결 앱',
            descHTML: '학생들의 입실, 퇴실 관리를 체계적으로 관리할 수 있어요!',
            order: 4,
          }),
          new SubScenario({
            id: '선생님추가',
            title: '선생님 추가 ',
            descHTML: `한 ${isSchoolflat ? '학교' : '학원'}에서 여러 선생님이 모두 사용하실 수 있어요!`,
            order: 5,
          }),
          new SubScenario({
            id: '반만들기',
            title: '반 만들기',
            descHTML: '등록된 학생들을 반 별로 나눠 관리도 가능해요!',
            order: 6,
          }),
          new SubScenario({
            id: '그룹보고서',
            title: '그룹 보고서',
            descHTML: '클릭 한 번으로 모든 학생들의 학습 보고서를 만들 수 있어요!',
            order: 7,
          }),
          new SubScenario({
            id: '다른기관학습지',
            title: '다른 기관 학습지',
            descHTML: '대치동, 목동 수학 학원들은 어떤 문제로 수업할까? 궁금하시다면!',
            order: 8,
          }),
          new SubScenario({
            id: '학생학부모앱',
            title: '학생/학부모앱',
            descHTML: '학부모님을 위한 공지, 학생의 출결 현황도 체계적으로 전할 수 있어요!',
            order: 9,
          }),
        ],
        'id' as keyof SubScenario,
      ),
    )
    this.bonusScenarioColl = new CheckableColl<Scenario>(
      CheckableMap.fromArr(
        [
          new Scenario({
            id: ScenarioService.scenarioIds.오답학습지출제,
            프로필: {
              descHTML: () => ``, // 보너스라서 여기 바라볼일 없음
            },
            시나리오카드: {
              title: '취약 유형 관리',
              descHTML: `<span>학생마다 틀린 문제 취약 유형을<br/>꽉 잡아보세요!</span>`,
            },
            완료팝업: {
              title: '취약 유형 관리 정복!',
              descHTML: `<span>단원˙기간별 취약 유형 관리로 오답 관리를 넘어선 ${commonRepo.appKorName}을 활용해보세요!<br />더 알아보시고 싶은 기능이 있다면 카드의 '좋아요'를 눌러주세요!</span>`,
              ctaButton: {
                text: '더 알아보고 싶은 기능 좋아요 누르러가기',
                width: 262,
              },
            },
            기능설명팝업: {
              preTitle: '4단계 모두 정복하셨다면?',
              title: `보너스! 오답 학습지 ${tJosa('출제')}해보기`,
              type: '이미지',
              resourceUrl: s3URL.mathflatAssetsBucket.teacher(
                'images/scenario/scenario_popup_make_wrong_worksheet.png',
              ),
              ctaButton: {
                text: `오답 학습지 ${tJosa('출제')}해보기`,
                onClick: async (scenarioId) => {
                  const lastScoredStudentId =
                    await scenarioService.fetchLastScoredStudentIdHandler()
                  if (lastScoredStudentId) {
                    const path = generatePath(RouteName.lessonWorksheetStudent, {
                      studentId: lastScoredStudentId,
                    })
                    this.scenarioPointerAt = { 단계: scenarioId, path }
                    commonRepo.router.push(path)
                    scrollToTop()
                  }
                },
                width: 159,
              },
            },
            order: 0,
          }),
        ],
        'id' as keyof Scenario,
      ),
    )
  }

  setProfileShow = (isShow: boolean) => {
    if (this.enabled) {
      this._isProfileShow = isShow
    } else {
      this._isProfileShow = false
    }
  }

  getScenarioStatus(id: Scenario['id']) {
    if (this.isFinishedScenario(id)) {
      return '완료'
    }
    if (id === this.nextScenario.id) {
      return '진행가능'
    }
    return '진행전'
  }

  isFinishedScenario(id: Scenario['id']) {
    return this.scenarioColl.isChecked(id) || this.bonusScenarioColl.isChecked(id)
  }

  async fetchUserScenarioHandler() {
    if (!this.enabled) return
    this._fetchUserScenario()
  }

  private async _fetchUserScenario() {
    try {
      const finshedIdSet = await scenarioController.fetchUserScenario()
      runInAction(() => {
        this.scenarioColl.toArr.forEach((scenario) => {
          if (finshedIdSet.has(scenario.id)) {
            this.scenarioColl.setCheck(scenario.id)
          } else {
            this.scenarioColl.deleteCheck(scenario.id)
          }
        })
        this.subScenarioColl.toArr.forEach((subScenario) => {
          if (finshedIdSet.has(subScenario.id)) {
            this.subScenarioColl.setCheck(subScenario.id)
          } else {
            this.subScenarioColl.deleteCheck(subScenario.id)
          }
        })
        this.bonusScenarioColl.toArr.forEach((bonusScenario) => {
          if (finshedIdSet.has(bonusScenario.id)) {
            this.bonusScenarioColl.setCheck(bonusScenario.id)
          } else {
            this.bonusScenarioColl.deleteCheck(bonusScenario.id)
          }
        })
      })
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  private async _postScenario(id: Scenario['id'] | SubScenario['id'], cb?: () => Promise<void>) {
    if (!this.enabled) return
    try {
      await scenarioController.postScenario(id)
      await cb?.()
      await this._fetchUserScenario()
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  async fetchLastAssginedStudentIdHandler() {
    if (!this.enabled) return
    const lastAssignedStudentId = await scenarioController.fetchLastAssginedStudentId()
    return lastAssignedStudentId
  }

  async fetchLastScoredStudentIdHandler() {
    if (!this.enabled) return
    const lastScoredStudentId = await scenarioController.fetchLastScoredStudentId()
    return lastScoredStudentId
  }

  checkIfValidStep(id: Scenario['id']) {
    switch (id) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore

      case '보고서만들기': {
        if (!this.isFinishedScenario('학습지채점하기')) {
          return false
        }
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-fallthrough
      case '학습지채점하기': {
        if (!this.isFinishedScenario('학습지출제하기')) {
          return false
        }
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-fallthrough
      case '학습지출제하기': {
        if (!this.isFinishedScenario('학습지만들기')) {
          return false
        }
      }
    }
    return true
  }

  async postScenarioAndLoadCompletePopupHanlder(id: Scenario['id'], cb?: () => Promise<void>) {
    if (!this.enabled) return
    // 미완된 카드이고, 시나리오 순서에 맞는지 체크
    if (!this.isFinishedScenario(id) && this.checkIfValidStep(id)) {
      await this._postScenario(id, cb)
      // iorad와 중복되는 로직이라 iorad가 적용이 안된 schoolflat에서만 팝업 노출
      if (commonRepo.isSchoolflat) {
        this._loadCompletePopup(id)
      }
    }

    if (this.scenarioPointerAt.단계 === id) {
      this.scenarioPointerAt = { 단계: undefined }
    }
  }

  private _loadCompletePopup(id: Scenario['id']) {
    if (!this.enabled) return

    this.scenarioPointerAt = { 단계: undefined }

    const scenario = this.scenarioColl.get(id) ?? this.bonusScenarioColl.get(id)
    if (scenario) {
      modalService.openModal(
        <ScenarioCompletePopup
          {...scenario.완료팝업}
          onClick={() => {
            modalService.closeModal()
            mixpanelClient.track('T99-03_정복_완료팝업_다음버튼', { 단계: id })

            if (this.nextScenario) {
              modalService.openModal(<ScenarioPopup scenario={this.nextScenario} />)
              mixpanelClient.track('T99-03_정복_설명팝업_노출', { 단계: this.nextScenario.id })
              return
            }
          }}
        />,
        {
          hasCloseButton: false,
          onClose: () => {
            mixpanelClient.track('T99-03_정복_완료팝업_닫기', { 단계: id })
            if (scenario.id === '오답학습지출제') {
              this.setProfileShow(true)
            }
            return true
          },
        },
      )
      mixpanelClient.track('T99-03_정복_완료팝업_노출', { 단계: id })
    }
  }
  async toggleSubScenarioHandler(id: SubScenario['id']) {
    if (!this.enabled) return

    if (this.subScenarioColl.isChecked(id)) {
      await this._unLikeSubScenario(id)
      mixpanelClient.track('T99-03_정복_좋아요카드_좋아요취소', { 단계: id })
    } else {
      await this._postSubScenario(id)
      mixpanelClient.track('T99-03_정복_좋아요카드_좋아요', { 단계: id })
    }
  }

  private async _postSubScenario(id: SubScenario['id']) {
    if (!this.enabled) return

    const prev = this.subScenarioColl.isChecked(id)
    this.subScenarioColl.setCheck(id) // optimistic update

    try {
      await this._postScenario(id)
    } catch (e) {
      console.error(e)
      if (prev) {
        this.subScenarioColl.setCheck(id)
      } else {
        this.subScenarioColl.deleteCheck(id)
      }
    }
  }

  async _unLikeSubScenario(id: SubScenario['id']) {
    if (!this.enabled) return

    const prev = this.subScenarioColl.isChecked(id)
    this.subScenarioColl.deleteCheck(id) // optimistic update

    try {
      await scenarioController.unLikeSubScenario(id)
    } catch (e) {
      console.error(e)
      if (prev) {
        this.subScenarioColl.setCheck(id)
      } else {
        this.subScenarioColl.deleteCheck(id)
      }
    }
  }
}

function scrollToTop() {
  const el = document.querySelector('.table-virtual-body > div')
  if (el) {
    el.scrollTo({ top: 0 })
  }
}

export const scenarioService = new ScenarioService()
